import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, FolderTree, Loader2, Mail, Scale, ShieldCheck } from "lucide-react";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AiTreeCard } from "@/components/admin/AiTreeCard";
import { AdminClassifyRules } from "@/components/admin/AdminClassifyRules";
import { AdminDigest } from "@/components/admin/AdminDigest";
import { AdminMailboxes } from "@/components/admin/AdminMailboxes";
import { AdminAccountant, AdminInbox, AdminRoleModels } from "@/components/admin/AdminAutomation";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function AdminAlerts() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thr, setThr] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then((r) => { setS(r.data); setThr(r.data.approval_threshold ?? ""); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const remindNow = async () => {
    try { const { data } = await api.post("/admin/approvals/remind"); toast.success(`${data.reminded} relance(s) envoyée(s)`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveThreshold = async (clear) => {
    try {
      const body = clear || thr === "" ? { clear_threshold: true } : { approval_threshold: Number(thr) };
      const { data } = await api.put("/admin/settings", body);
      setS({ ...s, ...data }); setThr(data.approval_threshold ?? "");
      toast.success(data.approval_threshold != null ? "Seuil enregistré" : "Validation direction exigée pour tous les montants");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async (v) => {
    try { const { data } = await api.put("/admin/settings", { alert_recipients: v }); setS({ ...s, ...data }); toast.success("Paramètre enregistré"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/alerts/run"); toast.success(`${data.documents} document(s) concerné(s), ${data.emails_queued} email(s) en cours d'envoi`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggleClassify = async (v) => {
    try { const { data } = await api.put("/admin/settings", { auto_classify: v }); setS({ ...s, ...data }); toast.success(v ? "Classement automatique activé" : "Classement automatique désactivé"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const classifyAll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/auto-classify-all"); toast.success(`Classement lancé pour ${data.started} document(s) à la racine, cela peut prendre quelques minutes`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border rounded-xl p-5 space-y-4 lg:col-span-2" data-testid="auto-classify-card">
        <h3 className="font-display font-bold flex items-center gap-2"><FolderTree className="h-4 w-4 text-[#1E3A8A]" />Classement automatique</h3>
        <p className="text-sm text-slate-600">Un document déposé à la racine (ou reçu par email) est analysé par l'IA puis rangé automatiquement selon les règles de la société, ou dans une arborescence créée par l'IA quand aucune règle ne convient. Les noms de fournisseurs proches (ex. « BRIDGESTONE FRANCE SAS » et « Bridgestone France ») partagent le même dossier. Un document déposé directement dans un dossier reste là où il a été placé.</p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Switch checked={s.auto_classify} onCheckedChange={toggleClassify} data-testid="auto-classify-switch" />
            {s.auto_classify ? "Activé" : "Désactivé"}
          </label>
          <Button variant="outline" onClick={classifyAll} disabled={busy} data-testid="auto-classify-all-btn">Classer maintenant les documents non rangés</Button>
        </div>
        <div className="border-t pt-4"><AiTreeCard /></div>
        <div className="border-t pt-4"><AdminClassifyRules /></div>
      </div>
      <AdminDigest />
      <AdminAccountant />
      <AdminInbox />
      <AdminMailboxes />
      <AdminRoleModels />
      <div className="bg-white border rounded-xl p-5 space-y-4 lg:col-span-2" data-testid="approval-threshold-card">
        <h3 className="font-display font-bold flex items-center gap-2"><Scale className="h-4 w-4 text-[#1E3A8A]" />Validation par montant</h3>
        <p className="text-sm text-slate-600">Sous ce montant TTC, une facture ou un devis est définitivement validé dès l'accord du responsable. À partir de ce montant, ou si le montant n'a pas été reconnu, la direction doit aussi valider.</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative sm:w-56">
            <Input type="number" min="0" step="0.01" value={thr} onChange={(e) => setThr(e.target.value)} placeholder="Ex. 1500" className="pr-8" data-testid="approval-threshold-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
          </div>
          <Button onClick={() => saveThreshold(false)} data-testid="approval-threshold-save">Enregistrer</Button>
          {s.approval_threshold != null && <Button variant="ghost" onClick={() => saveThreshold(true)} data-testid="approval-threshold-clear">Toujours exiger la direction</Button>}
        </div>
        <p className="text-xs font-semibold" data-testid="approval-threshold-status">
          {s.approval_threshold != null ? `Seuil actuel : ${fmtMoney(s.approval_threshold)} TTC` : "Aucun seuil : la direction valide toutes les demandes."}
        </p>
      </div>
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-display font-bold flex items-center gap-2"><BellRing className="h-4 w-4 text-amber-600" />Alertes d'échéance</h3>
        <p className="text-sm text-slate-600">Chaque jour à 7h (heure de Paris), un email est envoyé 30 jours puis 7 jours avant l'échéance d'un contrat ou d'une facture. Une notification apparaît aussi dans l'application.</p>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Destinataires</label>
          <Select value={s.alert_recipients} onValueChange={save}>
            <SelectTrigger className="mt-1" data-testid="alerts-recipients-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="owner_admins">Propriétaire du document et administrateurs</SelectItem>
              <SelectItem value="all_access">Toutes les personnes ayant accès au document</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={runNow} disabled={busy} data-testid="alerts-run-now-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Vérifier et envoyer maintenant
        </Button>
        <div className="border-t pt-4 space-y-2">
          <div className="text-sm font-semibold">Relances de validation</div>
          <p className="text-sm text-slate-600">Une demande sans décision depuis 48 h déclenche un email de relance aux valideurs concernés, puis toutes les 48 h jusqu'à la décision.</p>
          <Button variant="outline" onClick={remindNow} data-testid="approvals-remind-now-btn">Relancer maintenant</Button>
        </div>
      </div>
      <div className="bg-white border rounded-xl p-5 space-y-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Mail className="h-4 w-4 text-[#1E3A8A]" />Messagerie et sécurité</h3>
        <div className="flex items-center justify-between text-sm"><span>Envoi d'emails (SMTP)</span>
          <span className={`font-semibold ${s.mail_configured ? "text-emerald-700" : "text-red-600"}`} data-testid="smtp-status">{s.mail_configured ? "Configuré" : "Non configuré"}</span></div>
        <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Code email à chaque connexion</span>
          <span className="font-semibold text-emerald-700">{s.mfa_required ? "Activé" : "Désactivé"}</span></div>
        <p className="text-xs text-slate-500">Les échecs d'envoi sont consignés côté serveur. Vérifiez l'identifiant et le mot de passe de la messagerie si aucun email n'arrive.</p>
      </div>
    </div>
  );
}
